import React, { useState, useEffect } from 'react';
import { TenantService } from '../services/TenantService';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import ContactSubmissions from './ContactSubmissions';
import UserManagement from './UserManagement';
import WorkflowDesigner from './WorkflowDesigner';
import WorkflowAssignment from './WorkflowAssignment';
import WorkflowAnalytics from './WorkflowAnalytics';

import {
  Building2,
  Plus,
  Users,
  Database,
  Trash2,
  Edit3,
  Eye,
  AlertCircle,
  BarChart3,
  ChevronRight,
  Mail,
  MessageSquare,
  UserCog,
  GitBranch
} from 'lucide-react';

/**
 * Platform Dashboard - Notion-inspired admin interface for managing tenants
 * Clean, soft design with muted colors and spacious layouts
 */
export default function PlatformDashboard({ user, userProfile, onTenantSelect }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('tenants'); // 'tenants', 'contacts', 'users'

  useEffect(() => {
    if (activeTab === 'tenants') {
      loadTenants();
    }
    console.log('✅ Platform dashboard loaded');
  }, [activeTab]);

  /**
   * Load all tenants for platform admin
   */
  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Loading all tenants...');
      const allTenants = await TenantService.getAllTenants();
      // Filter out Demo tenant from platform admin view
      const filtered = allTenants.filter(
        t => t.id !== 'demo' && (t.name || '').toLowerCase() !== 'demo'
      );
      setTenants(filtered);
      console.log(`✅ Loaded ${allTenants.length} tenants`);
      
    } catch (err) {
      console.error('❌ Failed to load tenants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle tenant creation with invitation
   */
  const handleCreateTenant = async (tenantData) => {
    try {
      console.log('🏢 Creating new tenant with invitation:', tenantData.name);
      const result = await TenantService.createTenantWithInvitation(tenantData);
      
      // Show success message with invitation link
      alert(`✅ Tenant created successfully!\n\nInvitation sent to: ${tenantData.adminEmail}\n\nInvitation link: ${result.invitationLink}\n\n(In production, this would be sent via email)`);
      
      // Refresh tenant list
      await loadTenants();
      setShowCreateModal(false);
      
      console.log('✅ Tenant created successfully:', result.tenant.companyId);
    } catch (err) {
      console.error('❌ Failed to create tenant:', err);
      setError(err.message);
    }
  };

  /**
   * Handle tenant deletion
   */
  const handleDeleteTenant = async (tenantId) => {
    try {
      console.log('🗑️ Deleting tenant:', tenantId);
      await TenantService.deleteTenant(tenantId);
      
      // Refresh tenant list
      await loadTenants();
      setShowDeleteModal(false);
      setSelectedTenant(null);
      
      console.log('✅ Tenant deleted successfully');
    } catch (err) {
      console.error('❌ Failed to delete tenant:', err);
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/30">
     <div className="max-w-[1600px] mx-auto px-8 py-10 space-y-10">
        
        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white rounded-xl p-1 border border-slate-200/60 w-fit">
          <button
            onClick={() => setActiveTab('tenants')}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'tenants'
                ? 'bg-slate-800 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <Building2 size={16} />
            OS
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'users'
                ? 'bg-slate-800 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <UserCog size={16} />
            User Management
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'contacts'
                ? 'bg-slate-800 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <MessageSquare size={16} />
            Contact Submissions
          </button>
          <button
            onClick={() => setActiveTab('workflows')}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'workflows'
                ? 'bg-slate-800 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
            }`}
          >
            <GitBranch size={16} />
            Workflows
          </button>
        </div>

        {/* OS Tab */}
        {activeTab === 'tenants' && (
          <>
            {/* Header Section */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold text-slate-800 tracking-tight">OS</h1>
                <p className="text-slate-600 text-sm">{tenants.length} {tenants.length === 1 ? 'company' : 'companies'}</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-slate-800 text-white px-4 py-2.5 rounded-lg hover:bg-slate-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-sm"
              >
                <Plus size={16} />
                New Tenant
              </button>
            </div>

            {/* Tenant Table */}
            <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <LoadingSpinner size="lg" />
                </div>
              ) : error ? (
                <div className="text-center py-16">
                  <div className="p-3 bg-rose-100 rounded-full w-fit mx-auto mb-4">
                    <AlertCircle className="text-rose-600" size={24} />
                  </div>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">Error Loading</h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">{error}</p>
                  <button
                    onClick={loadTenants}
                    className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                  >
                    Try Again
                  </button>
                </div>
              ) : tenants.length === 0 ? (
                <div className="text-center py-16">
                  <div className="p-3 bg-slate-100 rounded-full w-fit mx-auto mb-4">
                    <Building2 className="text-slate-400" size={24} />
                  </div>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">No OS Yet</h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">Create your first tenant to get started with the platform</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                  >
                    Create First Tenant
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200/60">
                        <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Company</th>
                        <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Industry</th>
                        <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                        <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Users</th>
                        <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Data Usage</th>
                        <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Created</th>
                        <th className="text-right px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.map((tenant, index) => (
                        <tr 
                          key={tenant.id}
                          className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors cursor-pointer group ${
                            index === tenants.length - 1 ? 'border-b-0' : ''
                          }`}
                          onClick={() => onTenantSelect(tenant.id)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
                                <Building2 size={14} className="text-slate-600" />
                              </div>
                              <div>
                                <div className="font-medium text-slate-900">{tenant.name}</div>
                                <div className="text-sm text-slate-500">{tenant.adminEmail || 'No admin email'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {tenant.industry || 'General'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                              tenant.status === 'active' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : tenant.status === 'inactive' 
                                ? 'bg-slate-100 text-slate-600' 
                                : 'bg-rose-100 text-rose-700'
                            }`}>
                              {tenant.status || 'unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Users size={14} className="text-slate-400" />
                              {tenant.userCount || 0}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Database size={14} className="text-slate-400" />
                              {tenant.dataUsageMB ? `${(tenant.dataUsageMB / 1024).toFixed(1)} GB` : '0 MB'}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onTenantSelect(tenant.id);
                                }}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
                                title="View Tenant"
                              >
                                <Eye size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Edit tenant:', tenant.id);
                                }}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
                                title="Edit Tenant"
                              >
                                <Edit3 size={14} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTenant(tenant);
                                  setShowDeleteModal(true);
                                }}
                                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-200"
                                title="Delete Tenant"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <UserManagement />
        )}

        {/* Contact Submissions Tab */}
        {activeTab === 'contacts' && (
          <ContactSubmissions />
        )}

        {/* Workflows Tab */}
        {activeTab === 'workflows' && (
          <WorkflowsTab tenants={tenants} />
        )}

        {/* Create Tenant Modal */}
        {showCreateModal && (
          <CreateTenantModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateTenant}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedTenant && (
          <DeleteTenantModal
            tenant={selectedTenant}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedTenant(null);
            }}
            onConfirm={() => handleDeleteTenant(selectedTenant.id)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Create Tenant Modal Component - Notion-style
 */
function CreateTenantModal({ onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    adminEmail: '',
    features: {
      chainto: false,
      analytics: true,
      reporting: true
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.adminEmail.trim()) {
      setError('Company name and admin email are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-xl border border-slate-200/60">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Create New Tenant</h2>
        <p className="text-sm text-slate-600 mb-8">Set up a new company workspace</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Company Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
              placeholder="Enter company name"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Industry
            </label>
            <select
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
              disabled={loading}
            >
              <option value="">Select industry...</option>
              <option value="Manufacturing">Manufacturing</option>
              <option value="Retail">Retail</option>
              <option value="Technology">Technology</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Finance">Finance</option>
              <option value="Logistics">Logistics</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Admin Email *
            </label>
            <input
              type="email"
              value={formData.adminEmail}
              onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
              placeholder="admin@company.com"
              disabled={loading}
            />
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Features
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.features.chainto}
                  onChange={(e) => setFormData({
                    ...formData,
                    features: { ...formData.features, chainto: e.target.checked }
                  })}
                  className="rounded border-slate-300 text-slate-600 focus:ring-slate-400/20"
                  disabled={loading}
                />
                <span className="text-sm text-slate-700">ChainTo Business Intelligence</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.features.analytics}
                  onChange={(e) => setFormData({
                    ...formData,
                    features: { ...formData.features, analytics: e.target.checked }
                  })}
                  className="rounded border-slate-300 text-slate-600 focus:ring-slate-400/20"
                  disabled={loading}
                />
                <span className="text-sm text-slate-700">Advanced Analytics</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.features.reporting}
                  onChange={(e) => setFormData({
                    ...formData,
                    features: { ...formData.features, reporting: e.target.checked }
                  })}
                  className="rounded border-slate-300 text-slate-600 focus:ring-slate-400/20"
                  disabled={loading}
                />
                <span className="text-sm text-slate-700">Reporting Suite</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-rose-800 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-200 text-sm font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all duration-200 disabled:opacity-50 text-sm font-medium shadow-sm"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Delete Tenant Confirmation Modal - Notion-style
 */
function DeleteTenantModal({ tenant, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-xl border border-slate-200/60">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-rose-100 rounded-lg">
            <AlertCircle className="text-rose-600" size={20} />
          </div>
          <h2 className="text-xl font-semibold text-slate-800">Delete Tenant</h2>
        </div>

        <p className="text-slate-600 mb-4">
          Are you sure you want to delete <strong className="text-slate-800">{tenant.name}</strong>? This action cannot be undone and will:
        </p>

        <ul className="text-sm text-slate-600 mb-8 space-y-2 bg-slate-50 rounded-xl p-4">
          <li className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
            Remove all tenant data and configurations
          </li>
          <li className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
            Delete all user accounts for this tenant
          </li>
          <li className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
            Remove access to all tenant features
          </li>
          <li className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
            Cannot be reversed
          </li>
        </ul>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-200 text-sm font-medium"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all duration-200 disabled:opacity-50 text-sm font-medium"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete Tenant'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Workflows Tab Component - Sub-tabs for Designer, Assignment, Analytics
 */
function WorkflowsTab({ tenants }) {
  const [workflowSubTab, setWorkflowSubTab] = useState('designer');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-800 tracking-tight">Workflow Management</h1>
        <p className="text-slate-600 text-sm">Design, assign, and monitor workflows across tenants</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setWorkflowSubTab('designer')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            workflowSubTab === 'designer'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Designer
        </button>
        <button
          onClick={() => setWorkflowSubTab('assignment')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            workflowSubTab === 'assignment'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Assignment
        </button>
        <button
          onClick={() => setWorkflowSubTab('analytics')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            workflowSubTab === 'analytics'
              ? 'bg-white text-slate-800 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Analytics
        </button>
      </div>

      {/* Sub-tab Content */}
      <div className="bg-white rounded-xl border border-slate-200/60 p-6">
        {workflowSubTab === 'designer' && <WorkflowDesigner />}
        {workflowSubTab === 'assignment' && <WorkflowAssignment tenants={tenants} />}
        {workflowSubTab === 'analytics' && <WorkflowAnalytics />}
      </div>
    </div>
  );
}